// Punto de venta: al elegir al cliente aparecen sus notas de venta confirmadas que aún no tienen
// boleta ni factura (pedido de Rodrigo, 2026-10-10). Se toma una con Enter y el punto de venta la
// cobra; las que se despachan quedan marcadas para no confundirse. La lista no dice boleta ni
// factura: el documento se elige en la caja al tomarla (Rodrigo, 2026-10-27).
import { useEffect, useRef, useState } from 'react';
import type { NotaListada } from '../api/contratos';
import { useNotas } from '../api/consultas';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';

export function useNotasPorRetirar(empresaId: string, clienteEntidadId: string | null) {
  return useNotas(
    empresaId,
    {
      clienteEntidadId: clienteEntidadId ?? undefined,
      estado: 'CONFIRMADA',
      porDocumentar: 'true',
      pagina: 1,
    },
    !!clienteEntidadId,
  );
}

export function NotasPorRetirar({
  cliente,
  notas,
  alTomar,
  alCerrar,
}: {
  cliente: string;
  notas: NotaListada[];
  alTomar: (n: NotaListada) => void;
  alCerrar: () => void;
}) {
  // Primero las que retira en el local; luego las de despacho (se pueden tomar igual: el cliente vino).
  const orden = [...notas].sort(
    (a, b) =>
      Number(b.entrega === 'RETIRO') - Number(a.entrega === 'RETIRO') ||
      b.fecha_negocio.localeCompare(a.fecha_negocio),
  );
  const [sel, setSel] = useState(0);
  const tabla = useRef<HTMLTableElement>(null);
  useEffect(() => tabla.current?.focus(), []);
  return (
    <Modal
      titulo={`Notas de venta por retirar · ${cliente}`}
      ancho="medio"
      alCerrar={alCerrar}
      pie={
        <>
          <button type="button" onClick={alCerrar}>
            Venta nueva (Esc)
          </button>
          <button
            type="button"
            className="primario"
            disabled={!orden.length}
            onClick={() => orden[sel] && alTomar(orden[sel])}
          >
            Tomar la nota (Enter)
          </button>
        </>
      }
    >
      <p className="ayuda">
        Confirmadas y sin documento emitido. Al tomarla eliges boleta o factura, se cobra y se emite
        aquí; lo reservado sale con el documento.
      </p>
      <table
        ref={tabla}
        tabIndex={0}
        className="grilla"
        aria-label="Notas por retirar"
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSel((x) => Math.min(x + 1, orden.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSel((x) => Math.max(x - 1, 0));
          } else if (e.key === 'Enter' && orden[sel]) {
            e.preventDefault();
            alTomar(orden[sel]);
          }
        }}
      >
        <thead>
          <tr>
            <th>Nota</th>
            <th>Fecha</th>
            <th>Entrega</th>
            <th className="num">Total</th>
          </tr>
        </thead>
        <tbody>
          {orden.map((n, i) => (
            <tr
              key={n.id}
              aria-selected={i === sel}
              onClick={() => setSel(i)}
              onDoubleClick={() => alTomar(n)}
              data-entrega={n.entrega ?? 'DESPACHO'}
            >
              <td className="mono">{n.numeroVisible}</td>
              <td>{fecha(n.fecha_negocio)}</td>
              <td>
                {n.entrega === 'RETIRO' ? (
                  <b>Retira en local</b>
                ) : (
                  <span className="insignia tono-aviso" title="Al tomarla pasa a retiro en local">
                    Despacho
                  </span>
                )}
              </td>
              <td className="num">{n.total ? pesos(n.total) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}
